import { Box } from '@mui/material';
import { DefaultMapping } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { Navigate, Route, Routes } from 'react-router';
import { PoolDocumentTitle, RouteTab, RouteTabs } from '@synergy/components-next';
import { SearchFilter } from '@synergy/components';
import { useSearchParams } from 'react-router-dom';
import { useState } from 'react';

import * as AttributeActions from '../../../common/actions/attributes-actions';
import * as CategoryActions from '../../../common/actions/category-actions';
import * as UnitActions from '../../../common/actions/unit-actions';
import { AnyThunkAction } from '../../../store/any-thunk-action';
import { AttributesMappingOverview } from './attributes/attributes-mapping-overview';
import { CategoriesMappingOverviewTable } from './categories/categories-mapping-overview-table';
import { MappingEditModal } from '../../../common/components/mapping-edit-modal/mapping-edit-modal';
import { MappingTab } from './mapping-tabs';
import { PaloOverview } from '../../../common/components/palo/palo-overview';
import { UnitsMappingOverview } from './units/units-mapping-overview';
import { defaultSearchParamsSelector } from '../../../store/default-searchparams-selector';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

export const MappingOverview = () => {
    const dispatch = useThunkDispatch();
    const defaultSearchParams = useAppSelector(defaultSearchParamsSelector);

    const { requesting: categoriesRequesting } = useAppSelector(state => state.categories);
    const { requesting: unitsRequesting } = useAppSelector(state => state.units);
    const [searchParams] = useSearchParams(defaultSearchParams);
    const [selectedMapping, setSelectedMapping] = useState<DefaultMapping>();
    const [editAction, setEditAction] = useState<((mapping: DefaultMapping) => AnyThunkAction) | null>(null);

    const getTabSearchParams = () => {
        const params = new URLSearchParams(searchParams);
        params.set('page', '1');
        return params;
    };

    const handleEditModalClose = () => {
        setSelectedMapping(undefined);
        setEditAction(null);
    };

    const handleCategoryMappingEdit = (mapping: DefaultMapping) => {
        setSelectedMapping(mapping);
        setEditAction(() => CategoryActions.updateDefaultMapping);
    };

    const handleUnitMappingEdit = (mapping: DefaultMapping) => {
        setSelectedMapping(mapping);
        setEditAction(() => UnitActions.updateDefaultMapping);
    };

    const handleAttributeMappingEdit = (mapping: DefaultMapping) => {
        setSelectedMapping(mapping);
        setEditAction(() => AttributeActions.updateDefaultMapping);
    };

    const handleEditSubmit = (defaultMapping: string[]) => {
        if (selectedMapping && editAction) {
            dispatch(editAction({ id: selectedMapping.id, defaultMapping }));
            handleEditModalClose();
        }
    };

    return (
        <PaloOverview
            requesting={categoriesRequesting || unitsRequesting}
            titleMessage={<FormattedMessage id="app.mapping.title" />}
            defaultSearchParams={defaultSearchParams}
            filters={
                <Box sx={{ width: 220 }}>
                    <SearchFilter />
                </Box>
            }
            tabs={
                <RouteTabs tabs={MappingTab}>
                    <RouteTab
                        value={MappingTab.Categories}
                        label={<FormattedMessage id={'app.mapping.categories'} />}
                        searchParams={getTabSearchParams()}
                    />
                    <RouteTab
                        value={MappingTab.Units}
                        label={<FormattedMessage id="app.mapping.units" />}
                        searchParams={getTabSearchParams()}
                    />
                    <RouteTab
                        value={MappingTab.Attributes}
                        label={<FormattedMessage id="app.mapping.attributes" />}
                        searchParams={getTabSearchParams()}
                    />
                </RouteTabs>
            }
        >
            <PoolDocumentTitle titleTranslationId={'app.mapping.title'} />
            <Routes>
                <Route
                    path={MappingTab.Categories}
                    element={<CategoriesMappingOverviewTable onEdit={handleCategoryMappingEdit} />}
                />
                <Route path={MappingTab.Units} element={<UnitsMappingOverview onEdit={handleUnitMappingEdit} />} />
                <Route
                    path={MappingTab.Attributes}
                    element={<AttributesMappingOverview onEdit={handleAttributeMappingEdit} />}
                />
                <Route path="*" element={<Navigate to={{ pathname: MappingTab.Categories }} replace={true} />} />
            </Routes>
            {selectedMapping && editAction && (
                <MappingEditModal
                    visible={!!selectedMapping}
                    onClose={handleEditModalClose}
                    onSubmit={handleEditSubmit}
                    mapping={selectedMapping.defaultMapping}
                />
            )}
        </PaloOverview>
    );
};
